/**
 * The editable copy of the website's Help page — `Just Treated Help.dc.html`.
 *
 * ## A schema of its own, beside the homepage's
 *
 * The homepage schema is frozen (owner, 2026-10-07). The Help page's topics
 * and answers are the same kind of thing — declared slots with a stored value
 * — so they live in the same store, drafts and publish button as everything
 * else, but in their own file and their own editor sections. Nothing here is
 * returned by `content/homepage`; the page reads `content/help`.
 *
 * ## Fixed topics, a fixed number of questions
 *
 * Eight topics, as the frame draws them, each with up to five questions. Each
 * topic's icon is design and stays in the web app; its words are here. An
 * editor rewrites a topic, blanks a question to remove it, or fills an empty
 * slot to add one. A topic whose label is blank, or which has no question
 * left, is not drawn.
 *
 * ## The shipped answers are true of the platform as built
 *
 * The frame's answers promise things nobody has decided: "within 48 hours",
 * "2 to 3 working days", "usually within 2 hours", "3 to 5 working days", a
 * voucher worth 10%, a partner phone number, a `help@` mailbox. None of them is
 * shipped (owner decision D11 is open). Where the frame states a number, the
 * answer here says what the platform really does and that the rest is "to be
 * confirmed"; the owner replaces that wording from the dashboard, without a
 * deployment, once the true terms exist.
 *
 * ## No runtime imports
 *
 * The web app imports this file through `@jt/platform-content/help` to draw
 * the page before (or without) the API, so it must compile to plain data.
 */
/** The eight topics, in the frame's order. The id is what the web app keys its icon on. */
export const HELP_TOPIC_IDS = [
    'orders',
    'rx',
    'appt',
    'pay',
    'rewards',
    'account',
    'safety',
    'partners',
];
/** Question slots per topic. Adding one is a line, not a migration. */
export const HELP_QUESTION_SLOTS = 5;
const TO_BE_CONFIRMED = 'to be confirmed';
/**
 * The copy the page ships with. Every sentence was checked against the
 * platform's code on 2026-11-01 — see each answer's own wording for where a
 * fact is still the owner's to give.
 */
const SEEDS = [
    {
        id: 'orders',
        label: 'Orders & delivery',
        sub: 'Track, collect or cancel',
        questions: [
            [
                'Where is my order?',
                'Open Orders to see where it has got to. Each order shows its status, and a delivery shows the courier once one has picked it up. Orders you placed on this device without logging in are listed there too.',
            ],
            [
                'Can I change or cancel my order?',
                'You can cancel from your order page until the pharmacy starts preparing it. After that it can no longer be cancelled on the website. An order cannot be changed once it is placed: cancel it and order again instead.',
            ],
            [
                'How does collection work?',
                'Choose Collection at checkout. Your order page tells you when the order is ready to collect and gives directions to the shop.',
            ],
            [
                'Something is missing or damaged',
                `Reporting a problem from your order page is coming soon. How to reach us about it in the meantime is ${TO_BE_CONFIRMED}.`,
            ],
            [
                'Who can accept a delivery for me?',
                `While your order is on its way, your order page shows a handover code. The courier asks for it at the door to complete the delivery. Who may accept a prescription medicine on your behalf is ${TO_BE_CONFIRMED}.`,
            ],
        ],
    },
    {
        id: 'rx',
        label: 'Prescriptions',
        sub: 'Medicines that need a prescriber',
        questions: [
            [
                'Can I order an NHS prescription?',
                'Not yet. NHS prescriptions, including NHS repeat prescriptions, are not available on Just Treated yet.',
            ],
            [
                'How do I get a prescription-only medicine?',
                'Through an online consultation. Answer the pharmacy’s questions and pay for your order; the pharmacy’s prescriber then reviews your answers before anything is dispensed.',
            ],
            ['Can I upload a private prescription?', 'Not on the website yet.'],
            [
                'Do I pay for NHS prescriptions?',
                'In England there is an NHS prescription charge for each item unless you are exempt. NHS prescriptions are free in Scotland, Wales and Northern Ireland.',
            ],
        ],
    },
    {
        id: 'appt',
        label: 'Appointments & consultations',
        sub: 'Bookings and online care',
        questions: [
            [
                'Is my appointment booked?',
                'A new appointment is a request until the shop accepts it. Bookings shows it as Requested until then, and as Booked once the shop has confirmed it.',
            ],
            [
                'How do I change or cancel an appointment?',
                'Open Bookings, choose the appointment and then Cancel. You can cancel an appointment that is requested or confirmed and has not happened yet. Changing the time is not available yet: cancel it and book a new slot.',
            ],
            [
                'How do online consultations work?',
                'You answer the pharmacy’s questions and pay when you order. The pharmacy’s prescriber reviews your answers before anything is dispensed. If they need to know more they ask you a question, which you can answer from Bookings.',
            ],
            [
                'My consultation was declined. What now?',
                `The prescriber’s reason is shown on the consultation in Bookings. The order is cancelled and the pharmacy asks for your payment to be refunded in full. How long the refund takes to reach your account is ${TO_BE_CONFIRMED}.`,
            ],
        ],
    },
    {
        id: 'pay',
        label: 'Payments & refunds',
        sub: 'Charges, fees and refunds',
        questions: [
            [
                'When am I charged?',
                'An order is marked paid only once our payment provider confirms the payment. Until then it shows as waiting for payment, and you can pay for it from your order page.',
            ],
            [
                'What fees will I pay?',
                'Any delivery fee is shown at checkout before you pay, and the total there is what you are charged.',
            ],
            [
                'I cancelled a paid order. Will I get my money back?',
                'Returning the money is the pharmacy’s to do. Your order page says “Refund not recorded yet” until the refund has been made, and then shows it.',
            ],
            [
                'When will I get my refund?',
                `A refund shows on your order once it has been made, and goes back to the way you paid. How long it then takes to reach your account is ${TO_BE_CONFIRMED}.`,
            ],
        ],
    },
    {
        // The frame's "Voucher cards" topic. The platform has no voucher or credit
        // domain (owner decision D8: Rewards is the PENNY tab only).
        id: 'rewards',
        label: 'Rewards',
        sub: 'PENNY points',
        questions: [
            [
                'How do PENNY points work?',
                'When you are logged in, eligible orders can earn PENNY points. PENNY is still being tested, so how points are earned and spent may change.',
            ],
            [
                'Do you have voucher cards or account credit?',
                'Not yet. Voucher cards and account credit are not available on Just Treated.',
            ],
        ],
    },
    {
        id: 'account',
        label: 'Account & privacy',
        sub: 'Login, details and data',
        questions: [
            [
                'I can’t log in',
                'Log in with a code sent to your email address, so there is no password to remember. If you use a password and have forgotten it, use the email code instead.',
            ],
            [
                'Do I need an account to order?',
                'Not always. You can check out as a guest, and those orders are listed in Orders on the same device. Some medicines need you to be logged in.',
            ],
            [
                'How do I update my details?',
                'Go to Your account, then Personal details. Your email address is how you log in, so it cannot be changed there.',
            ],
            [
                'How do I delete my account?',
                'Deleting your account, and downloading your data, from the website are coming soon.',
            ],
        ],
    },
    {
        id: 'safety',
        label: 'Medicine safety',
        sub: 'Side effects and recalls',
        questions: [
            [
                'I think I’m having a side effect',
                'Speak to a pharmacist or call NHS 111. In an emergency, call 999. You can also report a side effect to the MHRA through the Yellow Card scheme.',
            ],
            [
                'Is my medicine part of a recall?',
                `Check the MHRA’s alerts for medicines and medical devices. How we will contact you about a recall is ${TO_BE_CONFIRMED}.`,
            ],
            [
                'Can I return medicines?',
                `Medicines cannot be resold once they have left the pharmacy. Our returns policy for faulty or wrongly supplied items is ${TO_BE_CONFIRMED}. Any pharmacy can dispose of unwanted medicines safely.`,
            ],
        ],
    },
    {
        id: 'partners',
        label: 'Pharmacies & partners',
        sub: 'Help for businesses',
        questions: [
            [
                'How do I join Just Treated?',
                'Create a partner account in the Just Treated partner portal. Your registration is checked before your shop goes live.',
            ],
            [
                'I’m a partner and need support',
                `Log in to the partner portal. A partner support contact is ${TO_BE_CONFIRMED}.`,
            ],
        ],
    },
];
const CONTACT_SECTION = 'Help — contact';
/** The editor section a topic's blocks are listed under. */
const sectionFor = (seed) => `Help — ${seed.label}`;
function topicBlocks(seed) {
    const section = sectionFor(seed);
    const blocks = [
        {
            key: `help.${seed.id}.label`,
            kind: 'text',
            label: 'Topic name (blank hides the topic)',
            section,
            item: 'Topic',
            fallback: seed.label,
            maxLength: 60,
        },
        {
            key: `help.${seed.id}.sub`,
            kind: 'text',
            label: 'Topic line',
            section,
            item: 'Topic',
            fallback: seed.sub,
            maxLength: 80,
        },
    ];
    for (let slot = 1; slot <= HELP_QUESTION_SLOTS; slot += 1) {
        const seeded = seed.questions[slot - 1];
        const n = String(slot);
        blocks.push({
            key: `help.${seed.id}.q${n}`,
            kind: 'text',
            label: `Question ${n} (blank removes it)`,
            section,
            item: `Question ${n}`,
            fallback: seeded?.[0] ?? '',
            maxLength: 160,
        }, {
            key: `help.${seed.id}.a${n}`,
            kind: 'text',
            label: `Answer ${n}`,
            section,
            item: `Question ${n}`,
            fallback: seeded?.[1] ?? '',
            maxLength: 1200,
        });
    }
    return blocks;
}
export const HELP_BLOCKS = [
    ...SEEDS.flatMap(topicBlocks),
    {
        // Empty until the owner names a real, monitored mailbox. While it is
        // empty the page draws "Email us" as Coming soon, never a mailto: to an
        // address nobody reads.
        key: 'help.contact.email',
        kind: 'text',
        label: 'Support email address (blank shows “Email us” as coming soon)',
        section: CONTACT_SECTION,
        fallback: '',
        maxLength: 254,
    },
];
/** The editor's sections for the Help page, in page order. */
export const HELP_SECTIONS = [...SEEDS.map(sectionFor), CONTACT_SECTION];
/** Deliberately simple: one @, no spaces, a dot in the domain. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The shipped copy, keyed as the store keys it. */
export const HELP_FALLBACKS = Object.fromEntries(HELP_BLOCKS.map((block) => [block.key, block.fallback]));
/**
 * The Help page's content from a key/value map — published values, or the
 * fallbacks. A blank question, an answerless question, a topic with no name
 * and a topic with no questions are all left out rather than drawn empty.
 */
export function helpContent(values) {
    const value = (key) => (values[key] ?? HELP_FALLBACKS[key] ?? '').trim();
    const topics = [];
    for (const id of HELP_TOPIC_IDS) {
        const label = value(`help.${id}.label`);
        if (label === '')
            continue;
        const questions = [];
        for (let slot = 1; slot <= HELP_QUESTION_SLOTS; slot += 1) {
            const q = value(`help.${id}.q${String(slot)}`);
            const a = value(`help.${id}.a${String(slot)}`);
            if (q !== '' && a !== '')
                questions.push({ q, a });
        }
        if (questions.length === 0)
            continue;
        topics.push({ id, label, sub: value(`help.${id}.sub`), questions });
    }
    const email = value('help.contact.email');
    return { topics, ...(EMAIL.test(email) ? { contactEmail: email } : {}) };
}
//# sourceMappingURL=help-schema.js.map